import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import { ref, update, get } from 'firebase/database';
import { Spinner } from '../ui/Spinner';
import { WorkspaceShell } from '../ui/Workspace';
import Button from '../ui/Button';
import ProfilePreviewCard from './ProfilePreviewCard';
import { useNavigate } from 'react-router-dom';
import PersonalDetails from '../AddStudent/steps/PersonalDetails';
import EducationalDetails from '../AddStudent/steps/EducationalDetails';
import DocumentUpload from '../AddStudent/steps/DocumentUpload';
import WorkExperience from '../AddStudent/steps/WorkExperience';
import {
  StudentActionLink,
  StudentBadge,
  StudentCard,
  StudentPage,
  StudentProgressBar,
  StudentSection,
} from '../student/StudentUI';
import StudentIdCard from '../student/StudentIdCard';

const EditProfile = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    personalDetails: {
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      email: currentUser?.email || '',
      phone: currentUser?.phone || '',
      dateOfBirth: currentUser?.dateOfBirth || '',
      address: currentUser?.address || '',
    },
    education: {
      olSchool: '',
      olYear: '',
      alSchool: '',
      alYear: '',
      qualifications: [],
    },
    documents: {
      photo: null,
      photoURL: currentUser?.photoURL || '',
      certificates: [],
    },
    workExperience: [
      {
        company: '',
        position: '',
        duration: '',
        responsibilities: '',
      },
    ],
    publicProfile: {
      bio: '',
      instagram: '',
      facebook: '',
      website: '',
    },
  });

  const [photoURL, setPhotoURL] = useState(currentUser?.photoURL || '');
  const [profileCompletion, setProfileCompletion] = useState(0);
  const isStudent = currentUser?.role === 'student';

  useEffect(() => {
    const loadProfileData = async () => {
      if (currentUser) {
        try {
          // Get student data
          const studentRef = ref(db, `students/${currentUser.uid}`);
          const studentSnapshot = await get(studentRef);
          const studentData = studentSnapshot.val();

          if (studentData) {
            setFormData((prev) => ({
              personalDetails: {
                ...prev.personalDetails,
                ...studentData.personalDetails,
              },
              education: studentData.education || prev.education,
              documents: {
                ...prev.documents,
                ...studentData.documents,
              },
              workExperience: studentData.workExperience || prev.workExperience,
              publicProfile: {
                ...prev.publicProfile,
                ...(studentData.publicProfile || {}),
              },
            }));

            // Calculate profile completion
            const completion = calculateProfileCompletion(studentData);
            setProfileCompletion(completion);
          } else {
            // If no student data, initialize with user data
            setFormData((prev) => ({
              ...prev,
              personalDetails: {
                firstName: currentUser.firstName || '',
                lastName: currentUser.lastName || '',
                email: currentUser.email || '',
                phone: currentUser.phone || '',
                dateOfBirth: currentUser.dateOfBirth || '',
                address: currentUser.address || '',
              },
            }));
          }
          setPhotoURL(currentUser.photoURL || '');
        } catch (error) {
          console.error('Error loading profile data:', error);
          setError('Failed to load profile data');
        }
      }
    };

    loadProfileData();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      personalDetails: {
        ...prev.personalDetails,
        [name]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const userRef = ref(db, `users/${currentUser.uid}`);
      const studentRef = ref(db, `students/${currentUser.uid}`);

      const userUpdate = {
        firstName: formData.personalDetails.firstName,
        lastName: formData.personalDetails.lastName,
        displayName:
          `${formData.personalDetails.firstName} ${formData.personalDetails.lastName}`.trim(),
        phone: formData.personalDetails.phone,
        address: formData.personalDetails.address,
        updatedAt: new Date().toISOString(),
      };

      // Update both nodes simultaneously
      await Promise.all([
        update(userRef, userUpdate),
        update(studentRef, {
          personalDetails: {
            ...formData.personalDetails,
            updatedAt: new Date().toISOString(),
          },
          publicProfile: {
            ...formData.publicProfile,
          },
          updatedAt: new Date().toISOString(),
        }),
      ]);

      setSuccess(true);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpdate = async (newPhotoURL) => {
    try {
      const userRef = ref(db, `users/${currentUser.uid}`);
      await update(userRef, {
        photoURL: newPhotoURL,
      });
      setPhotoURL(newPhotoURL);
      setSuccess(true);
    } catch (error) {
      console.error('Error updating profile picture:', error);
      setError('Failed to update profile picture. Please try again.');
    }
  };

  const handleCompleteProfile = () => {
    setShowProfileCompletion(true);
  };

  const updateFormData = (stepData) => {
    setFormData((prevData) => {
      const newData = { ...prevData };

      // Handle each section separately
      Object.keys(stepData).forEach((key) => {
        if (typeof stepData[key] === 'object' && stepData[key] !== null) {
          newData[key] = {
            ...prevData[key],
            ...stepData[key],
          };
        } else {
          newData[key] = stepData[key];
        }
      });

      return newData;
    });
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmitProfile = async () => {
    try {
      setLoading(true);
      setError('');

      const userRef = ref(db, `users/${currentUser.uid}`);
      const studentRef = ref(db, `students/${currentUser.uid}`);

      // Basic user info for users node
      const userUpdate = {
        firstName: formData.personalDetails.firstName,
        lastName: formData.personalDetails.lastName,
        displayName:
          `${formData.personalDetails.firstName} ${formData.personalDetails.lastName}`.trim(),
        email: formData.personalDetails.email,
        phone: formData.personalDetails.phone,
        address: formData.personalDetails.address,
        photoURL: formData.documents.photoURL || currentUser.photoURL,
        role: 'student',
        updatedAt: new Date().toISOString(),
      };

      // Detailed profile info for students node
      const studentUpdate = {
        personalDetails: {
          ...formData.personalDetails,
          photoURL: formData.documents.photoURL || currentUser.photoURL,
        },
        education: formData.education,
        documents: {
          ...formData.documents,
          certificates: formData.documents.certificates || [],
        },
        workExperience: formData.workExperience || [],
        publicProfile: {
          ...formData.publicProfile,
        },
        updatedAt: new Date().toISOString(),
        userId: currentUser.uid,
        role: 'student',
        status: 'active',
      };

      // Update both nodes simultaneously
      await Promise.all([
        update(userRef, userUpdate),
        update(studentRef, studentUpdate),
      ]);

      setSuccess(true);
      setShowProfileCompletion(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to complete profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderProfileCompletionStep = () => {
    const StepComponent = (() => {
      switch (currentStep) {
        case 1:
          return (
            <PersonalDetails
              formData={formData.personalDetails}
              updateFormData={(data) =>
                updateFormData({ personalDetails: data })
              }
            />
          );
        case 2:
          return (
            <EducationalDetails
              formData={formData.education}
              updateFormData={(data) => updateFormData({ education: data })}
            />
          );
        case 3:
          return (
            <DocumentUpload
              formData={formData.documents}
              updateFormData={(data) => updateFormData({ documents: data })}
              studentId={currentUser.uid}
            />
          );
        case 4:
          return (
            <WorkExperience
              formData={formData.workExperience}
              updateFormData={(data) =>
                updateFormData({ workExperience: data })
              }
            />
          );
        default:
          return null;
      }
    })();

    return (
      <div className="space-y-6">
        {StepComponent}

        <div className="flex justify-between gap-3 pt-6">
          {currentStep > 1 ? (
            <Button variant="outline" type="button" onClick={handlePrevious}>
              Previous
            </Button>
          ) : (
            <span />
          )}

          {currentStep < 4 ? (
            <Button type="button" className="ml-auto" onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button type="button" className="ml-auto" onClick={handleSubmitProfile}>
              Submit
            </Button>
          )}
        </div>
      </div>
    );
  };

  const calculateProfileCompletion = (studentData) => {
    if (!studentData) return 0;

    let completed = 0;
    let total = 4; // Total sections: Personal, Education, Documents, Work Experience

    // Check Personal Details
    if (studentData.personalDetails) {
      const personalFields = [
        'firstName',
        'lastName',
        'phone',
        'dateOfBirth',
        'address',
      ];
      const filledFields = personalFields.filter(
        (field) => studentData.personalDetails[field],
      );
      if (filledFields.length >= 3) completed++; // Consider complete if 3 or more fields filled
    }

    // Check Education
    if (
      studentData.education &&
      (studentData.education.olSchool ||
        studentData.education.alSchool ||
        (studentData.education.qualifications &&
          studentData.education.qualifications.length > 0))
    ) {
      completed++;
    }

    // Check Documents
    if (
      studentData.documents &&
      (studentData.documents.photoURL ||
        (studentData.documents.certificates &&
          studentData.documents.certificates.length > 0))
    ) {
      completed++;
    }

    // Check Work Experience
    if (
      studentData.workExperience &&
      studentData.workExperience.length > 0 &&
      studentData.workExperience[0].company
    ) {
      completed++;
    }

    return (completed / total) * 100;
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (showProfileCompletion) {
    return (
      <StudentPage
        title="Complete your profile"
        subtitle="Finish one small step at a time so your student record is ready."
        action={
          <StudentActionLink to="/dashboard" variant="secondary">
            Back to dashboard
          </StudentActionLink>
        }
      >
        <div className="mx-auto w-full max-w-4xl space-y-5">
          <StudentSection
            title="Step by step"
            description="Keep going until all four steps are done."
          >
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[1, 2, 3, 4].map((step) => (
                  <StudentCard
                    key={step}
                    className={currentStep === step ? 'border-primary-200 bg-primary-50' : 'bg-stone-50'}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-500">
                          Step {step}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-stone-900">
                          {step === 1
                            ? 'Personal'
                            : step === 2
                            ? 'Education'
                            : step === 3
                            ? 'Documents'
                            : 'Experience'}
                        </p>
                      </div>
                      <StudentBadge tone={currentStep > step ? 'success' : currentStep === step ? 'accent' : 'soft'}>
                        {currentStep > step ? 'Done' : currentStep === step ? 'Now' : 'Next'}
                      </StudentBadge>
                    </div>
                  </StudentCard>
                ))}
              </div>
              <StudentProgressBar value={(currentStep / 4) * 100} />
            </div>
          </StudentSection>

          {error ? (
            <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              Profile completed successfully.
            </div>
          ) : null}

          <StudentSection
            title="Current step"
            description="Only one section is shown so the form stays easy to follow."
          >
            {renderProfileCompletionStep()}
          </StudentSection>
        </div>
      </StudentPage>
    );
  }

  if (isStudent) {
    return (
      <StudentPage
        title="My profile"
        subtitle="Keep your contact details fresh and finish any missing items when you are ready."
        action={<Button onClick={handleCompleteProfile}>Complete profile</Button>}
      >
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[24rem_24rem_minmax(0,1fr)] xl:items-stretch">
          <div className="space-y-4">
            <div className="space-y-1 px-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
                Student ID card
              </p>
              <p className="text-sm leading-6 text-stone-600">
                Your academy ID preview is shown here in the same card layout used for printing.
              </p>
            </div>
            <StudentIdCard />
          </div>

          <div>
            <ProfilePreviewCard
              user={{
                ...currentUser,
                ...formData.personalDetails,
                photoURL,
              }}
              onPhotoUpdate={handlePhotoUpdate}
            />
          </div>

          <div className="space-y-5">
            <StudentSection
              title="Profile status"
              description="You can save the basics here or open the full guided flow."
            >
              <div className="space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-stone-500">Current completion</p>
                    <p className="text-3xl font-semibold text-stone-900">
                      {Math.round(profileCompletion)}%
                    </p>
                  </div>
                  <StudentBadge tone={profileCompletion >= 75 ? 'success' : 'accent'}>
                    {profileCompletion >= 75 ? 'Almost ready' : 'Needs details'}
                  </StudentBadge>
                </div>
                <StudentProgressBar value={Math.round(profileCompletion)} />
              </div>
            </StudentSection>

            <StudentSection
              title="Quick edit"
              description="This short form keeps the most-used details easy to update."
            >
              {error ? (
                <div className="mb-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              ) : null}
              {success ? (
                <div className="mb-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                  Profile updated successfully.
                </div>
              ) : null}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="First name"
                    name="firstName"
                    value={formData.personalDetails.firstName}
                    onChange={handleChange}
                  />
                  <Field
                    label="Last name"
                    name="lastName"
                    value={formData.personalDetails.lastName}
                    onChange={handleChange}
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.personalDetails.email}
                    onChange={handleChange}
                  />
                  <Field
                    label="Phone"
                    name="phone"
                    value={formData.personalDetails.phone}
                    onChange={handleChange}
                  />
                </div>
                <Field
                  label="Address"
                  name="address"
                  value={formData.personalDetails.address}
                  onChange={handleChange}
                />

                <div className="grid gap-4 rounded-[24px] border border-stone-200 bg-stone-50 px-4 py-4">
                  <div>
                    <p className="text-base font-semibold text-stone-900">
                      Public portfolio
                    </p>
                    <p className="mt-1 text-sm text-stone-600">
                      These details appear on your public student profile after you complete active study.
                    </p>
                  </div>
                  <TextAreaField
                    label="Short bio"
                    name="bio"
                    value={formData.publicProfile.bio}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        publicProfile: {
                          ...prev.publicProfile,
                          bio: event.target.value,
                        },
                      }))
                    }
                    rows={3}
                  />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Field
                      label="Instagram"
                      name="instagram"
                      value={formData.publicProfile.instagram}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          publicProfile: {
                            ...prev.publicProfile,
                            instagram: event.target.value,
                          },
                        }))
                      }
                    />
                    <Field
                      label="Facebook"
                      name="facebook"
                      value={formData.publicProfile.facebook}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          publicProfile: {
                            ...prev.publicProfile,
                            facebook: event.target.value,
                          },
                        }))
                      }
                    />
                    <Field
                      label="Website"
                      name="website"
                      value={formData.publicProfile.website}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          publicProfile: {
                            ...prev.publicProfile,
                            website: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
                  <Button variant="outline" type="button" onClick={handleCompleteProfile}>
                    Open guided steps
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              </form>
            </StudentSection>
          </div>
        </div>
      </StudentPage>
    );
  }

  return (
    <WorkspaceShell
      title="Edit Profile"
      description="Update how you appear in the directory and keep contact details current."
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-12 xl:col-span-4">
          <ProfilePreviewCard
            user={{
              ...currentUser,
              ...formData.personalDetails,
              photoURL,
            }}
            onPhotoUpdate={handlePhotoUpdate}
          />
        </div>

        <div className="lg:col-span-12 xl:col-span-8">
          {error && (
            <div className="mb-4 bg-red-50 p-4 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 p-4 rounded-md">
              <p className="text-sm text-green-700">
                Profile updated successfully!
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.personalDetails.firstName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.personalDetails.lastName}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.personalDetails.email}
                disabled
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.personalDetails.phone}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
              />
            </div>

            <div>
              <label
                htmlFor="address"
                className="block text-sm font-medium text-gray-700"
              >
                Address
              </label>
              <textarea
                name="address"
                id="address"
                rows={3}
                value={formData.personalDetails.address}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500 sm:text-sm"
              />
            </div>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0 flex-1 sm:mr-4">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    Profile Completion
                  </span>
                  <span className="text-sm font-medium text-pink-800">
                    {Math.round(profileCompletion)}%
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-gray-200">
                  <div
                    className="h-1.5 rounded-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-rose-500 transition-all duration-300"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleCompleteProfile}
                  type="button"
                  variant="outline"
                >
                  {profileCompletion < 100
                    ? 'Complete Profile'
                    : 'Update Details'}
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </WorkspaceShell>
  );
};

const Field = ({ label, name, value, onChange, type = 'text' }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-stone-700">{label}</span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="block min-h-[48px] w-full rounded-2xl border border-stone-300 px-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
    />
  </label>
);

const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  rows = 4,
}) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-stone-700">{label}</span>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      rows={rows}
      className="block w-full rounded-2xl border border-stone-300 px-4 py-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
    />
  </label>
);

export default EditProfile;
